<template>
  <div class="nfw">
    <ContentSkeleton v-if="loading && !loaded" variant="list" :rows="4" />
    <p v-else-if="error" class="dw-empty">{{ $t("dashboardWidgets.loadError") }}</p>
    <p v-else-if="!items.length" class="dw-empty">{{ t("empty") }}</p>
    <ul v-else class="nfw-list">
      <li v-for="n in items" :key="n.id" class="nfw-item">
        <span class="nfw-icon" aria-hidden="true">
          <i class="fas" :class="`fa-${iconOf(n)}`"></i>
        </span>
        <span class="nfw-body">
          <span class="nfw-line">
            <router-link v-if="linkOf(n)" :to="linkOf(n)" class="nfw-subject" :title="n.subject">{{ n.subject }}</router-link>
            <span v-else class="nfw-subject" :title="n.subject">{{ n.subject }}</span>
            <span v-if="n.count > 1" class="nfw-count" :title="t('repeated', { n: n.count })">×{{ n.count }}</span>
          </span>
          <span v-if="n.snippet" class="nfw-snippet">{{ n.snippet }}</span>
          <span class="nfw-when">{{ whenOf(n.at) }}</span>
        </span>
      </li>
    </ul>
  </div>
</template>

<script>
/**
 * Últimas notificaciones: los avisos que el usuario recibe por mail (pedidos de pago,
 * cobros, tickets, novedades), a mano en el dashboard. GET /api/users/my-notifications.
 */
import { wencesApi } from "@/api/wencesApi";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import { buildDynamicItemPrettySlug } from "@/utils/dynamicModelPaths";
import { formatTimestampSync } from "@/utils/dateFormatter";

const ICONS = {
  payment: "money-bill-wave",
  collect: "hand-holding-usd",
  supportticket: "life-ring",
  announcement: "bullhorn",
  shipping: "ship",
  proform: "file-invoice",
};

export default {
  name: "NotificationsWidget",
  components: { ContentSkeleton },
  props: {
    entry: { type: Object, required: true },
    config: { type: Object, default: () => ({}) },
  },
  data() {
    return { loading: false, loaded: false, error: false, items: [] };
  },
  computed: {
    menuItems() {
      return [];
    },
  },
  watch: {
    menuItems: {
      immediate: true,
      handler(items) {
        this.$emit("menu", items);
      },
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    t(key, params) {
      return this.$t(`dashboardWidgets.notifications.${key}`, params);
    },
    async load() {
      this.loading = true;
      this.error = false;
      try {
        const { data } = await wencesApi.get("/api/users/my-notifications", { params: { limit: 20 } });
        this.items = (data && data.data) || [];
        this.loaded = true;
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    iconOf(n) {
      return ICONS[String(n.contextModel || "").toLowerCase()] || "envelope";
    },
    linkOf(n) {
      const model = String(n.contextModel || "");
      if (!n.contextItemId || model.toLowerCase() === "announcement") return null;
      if (model.toLowerCase() === "supportticket") return `/support/tickets/${n.contextItemId}`;
      return `/i/${buildDynamicItemPrettySlug(model, n.contextItemId, "")}`;
    },
    /** «hace 5 min», «hace 3 h», y si pasó más de una semana, la fecha. */
    whenOf(at) {
      if (!at) return "";
      const diff = Date.now() - new Date(at).getTime();
      const min = Math.round(diff / 60000);
      if (min < 1) return this.t("justNow");
      if (min < 60) return this.t("minutesAgo", { n: min });
      const h = Math.round(min / 60);
      if (h < 24) return this.t("hoursAgo", { n: h });
      const d = Math.round(h / 24);
      if (d < 7) return this.t("daysAgo", { n: d });
      return formatTimestampSync(at);
    },
    onMenu() {},
  },
};
</script>

<style scoped>
.nfw-list { list-style: none; margin: 0; padding: 0; }
.nfw-item { display: flex; gap: 10px; padding: 8px 0; border-bottom: 1px solid var(--dw-border); }
.nfw-item:last-child { border-bottom: 0; }
.nfw-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--bs-primary-rgb), 0.12);
  color: var(--bs-primary);
  font-size: 0.78rem;
}
.nfw-body { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
.nfw-line { display: flex; align-items: baseline; gap: 6px; min-width: 0; }
.nfw-subject { font-weight: 600; font-size: 0.85rem; color: var(--dw-text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; min-width: 0; }
a.nfw-subject:hover { color: var(--bs-primary); }
.nfw-count { font-size: 0.7rem; color: var(--dw-muted); background: var(--dw-surface-2); border-radius: 999px; padding: 0 6px; }
.nfw-snippet {
  font-size: 0.78rem;
  color: var(--dw-muted);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.nfw-when { font-size: 0.7rem; color: var(--dw-muted); opacity: 0.85; }
</style>
